// render_chart (spec-9, ac-26/ac-27; spec-13): the agent hands us `{ type, ...data }`
// and we draw it with Nivo on the shared Insights theme/palette so an agent chart
// reads identically to the product's own. We validate the input shape and fail
// soft — a bad/unknown `type` (or a malformed graph) renders an inline error,
// never throws, so one malformed tool call can't blank the whole transcript
// (ac-27). spec-13 adds funnel / heatmap / sankey / area on a vibrant palette.

import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveFunnel, type FunnelDatum } from "@nivo/funnel";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import { ResponsiveSankey } from "@nivo/sankey";
import {
  insightsTheme,
  useChartPalette,
  useVibrantPalette,
  useSequentialRamp,
  type ChartPalette,
} from "../insights/theme";

// Agent-exposed types reuse the simple pie/line shapes (dec-4): pie, line, bar,
// funnel, area. heatmap + sankey also render here (dashboard-callers) but are NOT
// advertised to the model yet — see routes/agent.ts UI_TOOL_SCHEMAS.
type ChartType = "pie" | "line" | "bar" | "funnel" | "heatmap" | "sankey" | "area";
const CHART_TYPES: readonly ChartType[] = [
  "pie",
  "line",
  "bar",
  "funnel",
  "heatmap",
  "sankey",
  "area",
];

interface PieDatum {
  id: string;
  label?: string;
  value: number;
}
interface LineSeries {
  id: string;
  data: { x: string | number; y: number }[];
}
interface SankeyGraph {
  nodes: { id: string }[];
  links: { source: string; target: string; value: number }[];
}
interface ChartInput {
  type: ChartType;
  title?: string;
  // pie / funnel / line / bar / heatmap → array; sankey → { nodes, links }
  data?: PieDatum[] | LineSeries[] | SankeyGraph | Record<string, unknown>[];
  // bar
  keys?: string[];
  indexBy?: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Sankey is a directed graph and Nivo throws on a dangling link or a cycle, so we
// validate it up front and degrade to an inline error instead (ac-13/ac-27).
function validateSankey(data: unknown): { graph: SankeyGraph } | { error: string } {
  if (!isRecord(data) || !Array.isArray(data.nodes) || !Array.isArray(data.links)) {
    return { error: "Sankey input needs `{ nodes: [{id}], links: [{source,target,value}] }`." };
  }
  const ids = new Set<string>();
  for (const n of data.nodes) {
    if (isRecord(n) && n.id != null) ids.add(String(n.id));
  }
  const adjacency = new Map<string, string[]>();
  for (const l of data.links) {
    if (!isRecord(l)) return { error: "Each Sankey link must be an object." };
    const source = String(l.source);
    const target = String(l.target);
    if (!ids.has(source) || !ids.has(target)) {
      return { error: `Sankey link references an unknown node id ("${ids.has(source) ? target : source}").` };
    }
    (adjacency.get(source) ?? adjacency.set(source, []).get(source)!).push(target);
  }
  // Cycle detection (DFS with grey/black marks) — Nivo Sankey requires a DAG.
  const GREY = 1;
  const BLACK = 2;
  const marks = new Map<string, number>();
  const hasCycle = (node: string): boolean => {
    marks.set(node, GREY);
    for (const next of adjacency.get(node) ?? []) {
      const m = marks.get(next);
      if (m === GREY) return true;
      if (m !== BLACK && hasCycle(next)) return true;
    }
    marks.set(node, BLACK);
    return false;
  };
  for (const id of ids) {
    if (!marks.has(id) && hasCycle(id)) {
      return { error: "Sankey data contains a cycle; flows must be acyclic." };
    }
  }
  return { graph: { nodes: data.nodes as SankeyGraph["nodes"], links: data.links as SankeyGraph["links"] } };
}

// Validate the agent's input. Returns the typed input or an error string; never
// throws so the renderer can degrade gracefully.
function validate(input: unknown): { input: ChartInput } | { error: string } {
  if (!isRecord(input)) return { error: "Chart input must be an object." };
  const type = input.type;
  if (typeof type !== "string" || !CHART_TYPES.includes(type as ChartType)) {
    return {
      error: `Unsupported chart type${
        typeof type === "string" ? ` "${type}"` : ""
      }. Expected one of ${CHART_TYPES.join(", ")}.`,
    };
  }
  // Sankey carries an object graph, every other type carries a `data` array.
  if (type === "sankey") {
    const result = validateSankey(input.data);
    if ("error" in result) return result;
    return { input: { ...(input as unknown as ChartInput), type, data: result.graph } };
  }
  // The data array should be `data`, but tolerate common aliases the model picks.
  const data =
    input.data ?? input.values ?? input.series ?? input.dataset ?? input.points;
  if (!Array.isArray(data)) {
    return { error: "Chart input is missing a `data` array." };
  }
  return {
    input: { ...(input as unknown as ChartInput), type: type as ChartType, data },
  };
}

// Pie/funnel want [{ id, value }]; coerce from the labels/values the model tends
// to use (label/name/key for the slice, value/count/y for the magnitude).
function toPieData(data: ChartInput["data"]): PieDatum[] {
  return ((data as Record<string, unknown>[]) ?? []).map((d, i) => {
    const r = d as Record<string, unknown>;
    const id = String(r.id ?? r.label ?? r.name ?? r.key ?? i);
    const value = Number(r.value ?? r.count ?? r.y ?? 0);
    return { id, label: r.label != null ? String(r.label) : id, value };
  });
}

// Nivo's funnel wants an index-signature datum ({ [k]: string | number }); reuse
// the pie coercion then widen the shape so `label` is always present.
function toFunnelData(data: ChartInput["data"]): FunnelDatum[] {
  return toPieData(data).map((d) => ({ id: d.id, value: d.value, label: d.label ?? d.id }));
}

// Category labels (org names, slugs…) are often long; truncate so a row of them
// stays readable rather than overlapping.
function truncateLabel(v: unknown): string {
  const s = String(v);
  return s.length > 16 ? `${s.slice(0, 15)}…` : s;
}

// A small, stable, theme-aware series palette for the established multi-series
// product charts (pie/line/bar/area) — unchanged from spec-9.
function seriesColors(palette: ChartPalette): string[] {
  return [
    palette.accent,
    palette.human,
    palette.verified,
    palette.failing,
    palette.phase.specify,
    palette.phase.verify,
  ];
}

export function ChartBlock({ input }: { input: unknown }) {
  const palette = useChartPalette();
  const vibrant = useVibrantPalette();
  const [rampLow, rampHigh] = useSequentialRamp();
  const result = validate(input);

  if ("error" in result) {
    return (
      <div
        data-testid="chart-error"
        role="alert"
        className="rounded-xl border border-status-danger-border bg-status-danger-bg px-4 py-3 text-sm text-status-danger-text"
      >
        {result.error}
      </div>
    );
  }

  const { input: chart } = result;
  const colors = seriesColors(palette);

  function renderBody() {
    switch (chart.type) {
      case "pie":
        return (
          <ResponsivePie
            data={toPieData(chart.data)}
            // Wide left/right margins leave room for the arc link labels so long
            // org names aren't clipped at the chart edge.
            margin={{ top: 28, right: 120, bottom: 28, left: 120 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            colors={colors}
            theme={insightsTheme}
            borderWidth={1}
            arcLinkLabel={(d) => truncateLabel(d.label ?? d.id)}
            arcLinkLabelsSkipAngle={8}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabelsTextColor="rgb(var(--color-text-secondary, 100 116 139))"
            arcLabelsSkipAngle={12}
            animate
          />
        );
      case "line":
      case "area":
        // `area` is a stacked, filled line (dec-3/dec-4) — same contract, no new dep.
        return (
          <ResponsiveLine
            data={(chart.data as LineSeries[]) ?? []}
            xScale={{ type: "point" }}
            yScale={{
              type: "linear",
              min: 0,
              max: "auto",
              stacked: chart.type === "area",
            }}
            margin={{ top: 16, right: 24, bottom: 72, left: 48 }}
            colors={colors}
            theme={insightsTheme}
            curve="monotoneX"
            enablePoints={false}
            enableArea
            areaOpacity={chart.type === "area" ? 0.45 : 0.12}
            lineWidth={2.5}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickRotation: -35,
              format: truncateLabel,
            }}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            enableGridX={false}
            animate
          />
        );
      case "bar":
        return (
          <ResponsiveBar
            data={(chart.data as unknown as Record<string, string | number>[]) ?? []}
            keys={chart.keys ?? ["value"]}
            indexBy={chart.indexBy ?? "id"}
            margin={{ top: 16, right: 24, bottom: 80, left: 48 }}
            padding={0.3}
            colors={colors}
            borderRadius={2}
            theme={insightsTheme}
            enableLabel={false}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickRotation: -35,
              format: truncateLabel,
            }}
            axisLeft={{ tickSize: 0, tickPadding: 8 }}
            animate
          />
        );
      case "funnel":
        // Aggregate conversion / drop-off — data ordered widest→narrowest, each
        // stage a distinct vibrant hue, counts shown on each part (dec-1/dec-8).
        return (
          <ResponsiveFunnel
            data={toFunnelData(chart.data)}
            margin={{ top: 16, right: 24, bottom: 16, left: 24 }}
            direction="horizontal"
            colors={vibrant}
            theme={insightsTheme}
            borderWidth={12}
            labelColor={{ from: "color", modifiers: [["darker", 3]] }}
            beforeSeparatorLength={24}
            afterSeparatorLength={24}
            currentPartSizeExtension={8}
            animate
          />
        );
      case "heatmap":
        // Intensity over a 2-D grid (e.g. day×hour) — saturated sequential ramp so
        // busy cells pop (dec-2/dec-8).
        return (
          <ResponsiveHeatMap
            data={(chart.data as unknown as { id: string; data: { x: string; y: number }[] }[]) ?? []}
            margin={{ top: 28, right: 24, bottom: 48, left: 56 }}
            theme={insightsTheme}
            colors={{ type: "sequential", colors: [rampLow, rampHigh] }}
            emptyColor="rgb(var(--color-card-hover, 30 41 59))"
            borderRadius={2}
            borderWidth={1}
            borderColor="rgb(var(--color-surface, 255 255 255))"
            axisTop={{ tickSize: 0, tickPadding: 6, tickRotation: -35 }}
            axisLeft={{ tickSize: 0, tickPadding: 6 }}
            animate
          />
        );
      case "sankey":
        // Flows between states — nodes coloured from the vibrant palette (dec-4).
        return (
          <ResponsiveSankey
            data={chart.data as SankeyGraph}
            margin={{ top: 16, right: 120, bottom: 16, left: 24 }}
            colors={vibrant}
            theme={insightsTheme}
            nodeOpacity={1}
            nodeThickness={14}
            nodeBorderWidth={0}
            linkOpacity={0.4}
            linkContract={2}
            enableLinkGradient
            labelTextColor="rgb(var(--color-text-secondary, 100 116 139))"
            animate
          />
        );
      default:
        return null;
    }
  }

  return (
    <div
      className="rounded-xl border border-edge bg-surface p-4"
      data-testid="chart-block"
    >
      {chart.title && (
        <div className="mb-2 text-sm font-medium uppercase tracking-wider text-muted">
          {chart.title}
        </div>
      )}
      <div
        className="relative h-64"
        data-testid={`chart-${chart.type}`}
        role="img"
        aria-label={chart.title ?? `${chart.type} chart`}
      >
        {renderBody()}
      </div>
    </div>
  );
}
